import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api, Post } from '../utils/api';
import { useLanguage } from '../contexts/LanguageContext';

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').trim();
}

interface DesignPreset {
  background: string;
  accent: string;
  textColor: string;
  decorativeColor: string;
}

const designPresets: Record<string, DesignPreset> = {
  'red-gold': {
    background: 'linear-gradient(135deg, #B71C1C 0%, #D32F2F 40%, #FF5252 100%)',
    accent: '#FFD700',
    textColor: '#fff',
    decorativeColor: 'rgba(255,215,0,0.15)',
  },
  'blue-wave': {
    background: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 40%, #42A5F5 100%)',
    accent: '#E3F2FD',
    textColor: '#fff',
    decorativeColor: 'rgba(255,255,255,0.08)',
  },
  'green-nature': {
    background: 'linear-gradient(135deg, #1B5E20 0%, #388E3C 40%, #66BB6A 100%)',
    accent: '#C8E6C9',
    textColor: '#fff',
    decorativeColor: 'rgba(255,255,255,0.08)',
  },
  sunset: {
    background: 'linear-gradient(135deg, #BF360C 0%, #E65100 30%, #FF9800 100%)',
    accent: '#FFF3E0',
    textColor: '#fff',
    decorativeColor: 'rgba(255,255,255,0.1)',
  },
  'purple-night': {
    background: 'linear-gradient(135deg, #4A148C 0%, #7B1FA2 40%, #AB47BC 100%)',
    accent: '#E1BEE7',
    textColor: '#fff',
    decorativeColor: 'rgba(255,255,255,0.08)',
  },
  'teal-modern': {
    background: 'linear-gradient(135deg, #004D40 0%, #00796B 40%, #26A69A 100%)',
    accent: '#B2DFDB',
    textColor: '#fff',
    decorativeColor: 'rgba(255,255,255,0.08)',
  },
  'warm-earth': {
    background: 'linear-gradient(135deg, #3E2723 0%, #5D4037 40%, #8D6E63 100%)',
    accent: '#D7CCC8',
    textColor: '#fff',
    decorativeColor: 'rgba(255,255,255,0.08)',
  },
};

const fallbackSlides: Post[] = [
  {
    id: 1,
    title: 'Festive Feast 2026',
    subtitle: 'Annual Community Celebration',
    slug: 'festive-feast-2026',
    content: 'Join us for a grand celebration of culture, food, and community. Enjoy traditional performances, authentic cuisine, and family-friendly activities.',
    post_type: 'event',
    status: 'published',
    featured: 1,
    featured_design: 'red-gold',
    event_date: '2026-05-15',
    event_time: '5:00 PM - 9:00 PM',
    event_location: 'Irvine Marriott Grand Ballroom',
    category: 'Annual Gala',
    button1_text: 'Get Tickets',
    button1_url: '/events',
    button2_text: 'Learn More',
    button2_url: '/events',
    author_id: 1,
    created_at: '2026-01-01T00:00:00Z',
    updated_at: '2026-01-01T00:00:00Z',
  },
  {
    id: 2,
    title: 'Spring Cultural Festival',
    subtitle: 'Celebrating Heritage & Tradition',
    slug: 'spring-cultural-festival',
    content: 'Experience the beauty of Chinese culture through art, music, dance, and culinary traditions at our annual spring festival.',
    post_type: 'event',
    status: 'published',
    featured: 1,
    featured_design: 'blue-wave',
    event_date: '2026-04-20',
    event_time: '10:00 AM - 4:00 PM',
    event_location: 'Great Park, Irvine',
    category: 'Community',
    button1_text: 'Register Now',
    button1_url: '/events',
    button2_text: 'View Schedule',
    button2_url: '/schedule',
    author_id: 1,
    created_at: '2026-01-01T00:00:00Z',
    updated_at: '2026-01-01T00:00:00Z',
  },
  {
    id: 3,
    title: 'Youth Leadership Summit',
    subtitle: 'Empowering the Next Generation',
    slug: 'youth-leadership-summit',
    content: 'A full-day summit designed for young leaders to develop skills, network, and give back to the community.',
    post_type: 'event',
    status: 'published',
    featured: 1,
    featured_design: 'purple-night',
    event_date: '2026-06-10',
    event_time: '9:00 AM - 5:00 PM',
    event_location: 'UCI Student Center',
    category: 'Education',
    button1_text: 'Apply Now',
    button1_url: '/events',
    button2_text: 'Details',
    button2_url: '/events',
    author_id: 1,
    created_at: '2026-01-01T00:00:00Z',
    updated_at: '2026-01-01T00:00:00Z',
  },
];

function formatSlideDate(dateStr?: string, locale?: string): { month: string; day: string; year: string } {
  if (!dateStr) return { month: 'TBD', day: '', year: '' };
  const d = new Date(dateStr + 'T00:00:00');
  return {
    month: d.toLocaleDateString(locale === 'zh' ? 'zh-CN' : 'en-US', { month: 'short' }).toUpperCase(),
    day: String(d.getDate()),
    year: String(d.getFullYear()),
  };
}

export default function FeaturedCarousel() {
  const { locale, prefix } = useLanguage();
  const [slides, setSlides] = useState<Post[]>(fallbackSlides);
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const localeParam = locale === 'zh' ? '&locale=zh' : '&locale=en';
    api.get<{ posts: Post[] }>(`/posts?featured=true&status=published&limit=7${localeParam}`)
      .then((data) => {
        if (data.posts && data.posts.length > 0) {
          setSlides(data.posts.slice(0, 7));
        }
      })
      .catch(() => {});
  }, [locale]);

  const goTo = useCallback(
    (index: number) => {
      if (isTransitioning) return;
      setIsTransitioning(true);
      setCurrent(index);
      setTimeout(() => setIsTransitioning(false), 500);
    },
    [isTransitioning]
  );

  const next = useCallback(() => {
    goTo((current + 1) % slides.length);
  }, [current, slides.length, goTo]);

  const prev = useCallback(() => {
    goTo((current - 1 + slides.length) % slides.length);
  }, [current, slides.length, goTo]);

  useEffect(() => {
    timerRef.current = setInterval(next, 6000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [next]);

  const resetTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(next, 6000);
  };

  const slide = slides[current];
  const preset = designPresets[slide.featured_design || 'red-gold'] || designPresets['red-gold'];
  const dateInfo = formatSlideDate(slide.event_date, locale);
  const isImageMode = !!slide.featured_image_url && !slide.featured_design;

  const bgStyle: React.CSSProperties = isImageMode
    ? {
        backgroundImage: `linear-gradient(to right, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 50%, transparent 100%), url(${slide.featured_image_url})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : {
        background: preset.background,
      };

  const resolveLink = (url?: string) => {
    if (!url) return prefix + '/events';
    if (url.startsWith('http')) return url;
    return prefix + url;
  };

  return (
    <section style={{ position: 'relative', overflow: 'hidden' }}>
      <div
        style={{
          ...bgStyle,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          transition: 'background 0.5s ease',
          position: 'relative',
        }}
      >
        {/* Decorative circles for design mode */}
        {!isImageMode && (
          <>
            <div
              style={{
                position: 'absolute',
                right: -80,
                top: -80,
                width: 500,
                height: 500,
                borderRadius: '50%',
                background: preset.decorativeColor,
                pointerEvents: 'none',
              }}
            />
            <div
              style={{
                position: 'absolute',
                right: 100,
                bottom: -120,
                width: 350,
                height: 350,
                borderRadius: '50%',
                background: preset.decorativeColor,
                pointerEvents: 'none',
              }}
            />
            <div
              style={{
                position: 'absolute',
                left: '50%',
                top: '20%',
                width: 200,
                height: 200,
                borderRadius: '50%',
                background: preset.decorativeColor,
                pointerEvents: 'none',
              }}
            />
          </>
        )}

        <div
          className="container"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            alignItems: 'center',
            gap: 48,
            position: 'relative',
            zIndex: 2,
            paddingTop: 100,
            paddingBottom: 100,
          }}
        >
          {/* Left side content */}
          <div style={{ color: preset.textColor }}>
            {/* Date badge */}
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 12,
                marginBottom: 24,
                background: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(10px)',
                borderRadius: 'var(--radius-md)',
                padding: '12px 20px',
              }}
            >
              <div
                style={{
                  width: 50,
                  height: 55,
                  borderRadius: 'var(--radius-sm)',
                  background: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1,
                }}
              >
                <span style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: 1, opacity: 0.9 }}>
                  {dateInfo.month}
                </span>
                <span style={{ fontSize: '1.5rem', fontWeight: 800 }}>{dateInfo.day}</span>
              </div>
              <div>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, opacity: 0.9 }}>
                  {slide.event_date
                    ? new Date(slide.event_date + 'T00:00:00').toLocaleDateString(locale === 'zh' ? 'zh-CN' : 'en-US', {
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      })
                    : 'Date TBD'}
                </div>
                {slide.category && (
                  <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: 2 }}>
                    {slide.category}
                  </div>
                )}
              </div>
            </div>

            {/* Title */}
            <h1
              style={{
                fontSize: 'clamp(2.5rem, 5vw, 4rem)',
                fontWeight: 900,
                lineHeight: 1.1,
                marginBottom: 12,
                letterSpacing: '-1px',
              }}
            >
              {slide.title}
            </h1>

            {/* Subtitle */}
            {slide.subtitle && (
              <p
                style={{
                  fontSize: '1.3rem',
                  fontWeight: 500,
                  opacity: 0.9,
                  marginBottom: 16,
                }}
              >
                {slide.subtitle}
              </p>
            )}

            {/* Description */}
            {slide.content && (
              <p
                style={{
                  fontSize: '1.05rem',
                  lineHeight: 1.6,
                  opacity: 0.8,
                  marginBottom: 24,
                  maxWidth: 500,
                }}
              >
                {stripHtml(slide.content).length > 180 ? stripHtml(slide.content).substring(0, 180) + '...' : stripHtml(slide.content)}
              </p>
            )}

            {/* Location & time */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 32 }}>
              {slide.event_location && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.95rem', opacity: 0.85 }}>
                  <span>&#128205;</span>
                  <span>{slide.event_location}</span>
                </div>
              )}
              {slide.event_time && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.95rem', opacity: 0.85 }}>
                  <span>&#128337;</span>
                  <span>{slide.event_time}</span>
                </div>
              )}
            </div>

            {/* CTA Buttons */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {slide.button1_text && (
                <Link
                  to={resolveLink(slide.button1_url)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '14px 28px',
                    borderRadius: 'var(--radius-sm)',
                    background: '#fff',
                    color: '#D32F2F',
                    fontWeight: 700,
                    fontSize: '1rem',
                    transition: 'all 0.2s ease',
                    textDecoration: 'none',
                  }}
                >
                  {slide.button1_text}
                </Link>
              )}
              {slide.button2_text && (
                <Link
                  to={resolveLink(slide.button2_url)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '14px 28px',
                    borderRadius: 'var(--radius-sm)',
                    background: 'rgba(255,255,255,0.15)',
                    border: '2px solid rgba(255,255,255,0.4)',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '1rem',
                    transition: 'all 0.2s ease',
                    textDecoration: 'none',
                  }}
                >
                  {slide.button2_text}
                </Link>
              )}
            </div>
          </div>

          {/* Right side decorative */}
          <div
            className="carousel-right-side"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
            }}
          >
            {isImageMode && slide.featured_image_url ? (
              <img
                src={slide.featured_image_url}
                alt={slide.title}
                style={{
                  maxWidth: '100%',
                  maxHeight: 500,
                  borderRadius: 'var(--radius-lg)',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <div style={{ position: 'relative', width: '100%', height: 400 }}>
                {/* Decorative lanterns / shapes */}
                <div
                  style={{
                    position: 'absolute',
                    top: '10%',
                    right: '15%',
                    width: 120,
                    height: 160,
                    borderRadius: '50% 50% 50% 50% / 40% 40% 60% 60%',
                    background: preset.decorativeColor,
                    border: `3px solid ${preset.accent}`,
                    opacity: 0.6,
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    top: '30%',
                    right: '40%',
                    width: 80,
                    height: 110,
                    borderRadius: '50% 50% 50% 50% / 40% 40% 60% 60%',
                    background: preset.decorativeColor,
                    border: `2px solid ${preset.accent}`,
                    opacity: 0.4,
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    bottom: '15%',
                    right: '25%',
                    width: 100,
                    height: 140,
                    borderRadius: '50% 50% 50% 50% / 40% 40% 60% 60%',
                    background: preset.decorativeColor,
                    border: `2px solid ${preset.accent}`,
                    opacity: 0.5,
                  }}
                />
                {/* Large circle */}
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 220,
                    height: 220,
                    borderRadius: '50%',
                    border: `3px solid ${preset.accent}`,
                    opacity: 0.3,
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Navigation arrows */}
        {slides.length > 1 && (
          <>
            <button
              onClick={() => {
                prev();
                resetTimer();
              }}
              aria-label="Previous slide"
              style={{
                position: 'absolute',
                left: 20,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.15)',
                border: '2px solid rgba(255,255,255,0.3)',
                color: '#fff',
                fontSize: '1.3rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 10,
                transition: 'all 0.2s ease',
                backdropFilter: 'blur(4px)',
              }}
            >
              &#8249;
            </button>
            <button
              onClick={() => {
                next();
                resetTimer();
              }}
              aria-label="Next slide"
              style={{
                position: 'absolute',
                right: 20,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.15)',
                border: '2px solid rgba(255,255,255,0.3)',
                color: '#fff',
                fontSize: '1.3rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 10,
                transition: 'all 0.2s ease',
                backdropFilter: 'blur(4px)',
              }}
            >
              &#8250;
            </button>
          </>
        )}

        {/* Dot indicators */}
        {slides.length > 1 && (
          <div
            style={{
              position: 'absolute',
              bottom: 80,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 10,
              zIndex: 10,
            }}
          >
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => {
                  goTo(i);
                  resetTimer();
                }}
                aria-label={`Go to slide ${i + 1}`}
                style={{
                  width: i === current ? 32 : 10,
                  height: 10,
                  borderRadius: 5,
                  background: i === current ? '#fff' : 'rgba(255,255,255,0.4)',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                }}
              />
            ))}
          </div>
        )}

        {/* Scroll-down chevron */}
        <div
          style={{
            position: 'absolute',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            animation: 'bounceDown 2s infinite',
          }}
        >
          <button
            onClick={() => {
              window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
            }}
            aria-label="Scroll down"
            style={{
              background: 'none',
              border: 'none',
              color: 'rgba(255,255,255,0.6)',
              fontSize: '1.8rem',
              cursor: 'pointer',
            }}
          >
            &#8964;
          </button>
        </div>
      </div>

      <style>{`
        @keyframes bounceDown {
          0%, 100% { transform: translateX(-50%) translateY(0); }
          50% { transform: translateX(-50%) translateY(8px); }
        }
        @media (max-width: 768px) {
          .carousel-right-side { display: none !important; }
          .container {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>
  );
}
